/*
 * Managing product tags and category tags.
 *
 * These two catalogues are the whole of the metadata an article points at, which
 * is why MetadataKind has exactly two members and why this page has exactly two
 * tabs. A tab added here without a member added there would not compile.
 *
 * Every list offers "retire" alongside "delete", and says which one you probably
 * want. That is not hedging: IsActive exists on both tables precisely because
 * removing a tag from the editor is a different operation from destroying the
 * assignments and text that depend on it, and the destructive one is almost never
 * what someone reaching for "remove this tag" actually means. Delete is still there,
 * and still goes through the impact dialog.
 *
 * Release notes carry a product too (S42, 2026-10-12) - the same catalogue the
 * owner asked them to "reference" - so the product table counts them in a column
 * of their own, from the admin-only Product.releaseNoteCount. A product any
 * release note uses cannot be deleted (FK without cascade, and a blocker in the
 * impact report), which is one more reason the Delete tooltip points at Retire.
 *
 * Its words come from the chosen language's dictionary, `t.admin.site` (S48).
 */

import { useState } from 'react';
import type { Category, DeleteResult, MetadataKind, Product } from '@accotool-kb/shared';
import { isValidCategoryName, isValidSlug, toSlug } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { ErrorBanner, Field, Loading, Modal } from '../components/ui.js';
import type { siteEn } from '../i18n/admin/site.en.js';
import { useMessages } from '../i18n/language.js';
import { DeleteImpactDialog, type DeleteTarget } from './components/DeleteImpactDialog.js';

/**
 * One tab per catalogue, keyed by the shared MetadataKind so that a tab and the
 * delete target it opens name the same thing. A third catalogue would have to be
 * added to MetadataKind first, which is what stops this page drifting from the
 * impact machinery that consumes those names.
 */
type TabKey = MetadataKind;

const TABS: TabKey[] = ['product', 'category'];

export function MetadataPage(): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.metadata;
  const api = useAdminApi();
  const [tab, setTab] = useState<TabKey>('product');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const state = useAsync(() => api.metadata(), []);

  const onDeleted = (result: DeleteResult) => {
    setNotice(
      s.deleted(result.targetLabel, result.performed.map((step) => step.description).join(' ')),
    );
    state.reload();
  };

  if (state.loading && !state.data) return <Loading label={s.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{s.title}</h1>
          <p className="muted">{s.intro}</p>
        </div>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      <div className="tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
          >
            {s[key].tab}
          </button>
        ))}
      </div>

      {tab === 'product' && (
        <ProductsTab
          products={state.data.products}
          onChanged={state.reload}
          onDelete={setDeleteTarget}
        />
      )}
      {tab === 'category' && (
        <CategoriesTab
          categories={state.data.categories}
          onChanged={state.reload}
          onDelete={setDeleteTarget}
        />
      )}
      <DeleteImpactDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={onDeleted}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Product and category tags - the same shape, so one dialog serves both
// ---------------------------------------------------------------------------

function TagTable({
  kind,
  rows,
  onEdit,
  onNew,
  onDelete,
}: {
  kind: 'product' | 'category';
  rows: Array<{
    id: number;
    name: string;
    slug: string;
    isActive: boolean;
    articleCount: number;
    /** Products only: release notes carrying it, in every state. */
    releaseNoteCount?: number;
  }>;
  onEdit: (id: number) => void;
  onNew: () => void;
  onDelete: (target: DeleteTarget) => void;
}): JSX.Element {
  const s = useMessages().admin.site.metadata;
  // Only a product can carry a release note, so only its table has the column.
  const showReleaseNotes = kind === 'product';

  return (
    <>
      <div className="card-header">
        <p className="muted" style={{ margin: 0 }}>
          {s[kind].intro}
        </p>
        <button type="button" className="button button-primary" onClick={onNew}>
          {s[kind].newTag}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{s.name}</th>
                <th scope="col">{s.slug}</th>
                <th scope="col">{s.articles}</th>
                {showReleaseNotes && <th scope="col">{s.releaseNotes}</th>}
                <th scope="col">{s.state}</th>
                <th scope="col">
                  <span className="visually-hidden">{s.actions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>{row.articleCount}</td>
                  {showReleaseNotes && <td>{row.releaseNoteCount ?? 0}</td>}
                  <td>
                    {row.isActive ? (
                      <span className="badge badge-live">{s.active}</span>
                    ) : (
                      <span className="badge badge-hidden">{s.retired}</span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() => onEdit(row.id)}
                    >
                      {s.edit}
                    </button>{' '}
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      onClick={() =>
                        onDelete({ kind, targetId: String(row.id), label: row.name })
                      }
                      title={describeUse(row.articleCount, row.releaseNoteCount ?? 0, s)}
                    >
                      {s.delete}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={showReleaseNotes ? 6 : 5} className="muted">
                    {s[kind].none}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * The Delete button's tooltip: what uses the tag, when anything does. The
 * article-only sentence is unchanged; a product used by release notes says so
 * too, because those make the delete impossible rather than merely costly - a
 * product a release note carries is a blocker in the impact report.
 */
function describeUse(
  articleCount: number,
  releaseNoteCount: number,
  s: (typeof siteEn)['metadata'],
): string | undefined {
  if (articleCount === 0 && releaseNoteCount === 0) return undefined;
  if (releaseNoteCount === 0) return s.usedByArticles(articleCount);
  return s.usedByReleaseNotes(articleCount, releaseNoteCount);
}

function ProductsTab({
  products,
  onChanged,
  onDelete,
}: {
  products: Product[];
  onChanged: () => void;
  onDelete: (target: DeleteTarget) => void;
}): JSX.Element {
  const api = useAdminApi();
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const current = editing?.id ? products.find((row) => row.productId === editing.id) : undefined;

  return (
    <>
      <TagTable
        kind="product"
        rows={products.map((product) => ({
          id: product.productId,
          name: product.name,
          slug: product.slug,
          isActive: product.isActive,
          articleCount: product.articleCount ?? 0,
          releaseNoteCount: product.releaseNoteCount ?? 0,
        }))}
        onEdit={(id) => setEditing({ id })}
        onNew={() => setEditing({ id: null })}
        onDelete={onDelete}
      />

      {editing && (
        <TagDialog
          kind="product"
          initial={
            current
              ? { name: current.name, slug: current.slug, isActive: current.isActive }
              : null
          }
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            editing.id ? api.updateProduct(editing.id, input) : api.createProduct(input)
          }
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function CategoriesTab({
  categories,
  onChanged,
  onDelete,
}: {
  categories: Category[];
  onChanged: () => void;
  onDelete: (target: DeleteTarget) => void;
}): JSX.Element {
  const api = useAdminApi();
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const current = editing?.id
    ? categories.find((row) => row.categoryId === editing.id)
    : undefined;

  return (
    <>
      <TagTable
        kind="category"
        rows={categories.map((category) => ({
          id: category.categoryId,
          name: category.name,
          slug: category.slug,
          isActive: category.isActive,
          articleCount: category.articleCount ?? 0,
        }))}
        onEdit={(id) => setEditing({ id })}
        onNew={() => setEditing({ id: null })}
        onDelete={onDelete}
      />

      {editing && (
        <TagDialog
          kind="category"
          initial={
            current
              ? { name: current.name, slug: current.slug, isActive: current.isActive }
              : null
          }
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            editing.id ? api.updateCategory(editing.id, input) : api.createCategory(input)
          }
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function TagDialog({
  kind,
  initial,
  onClose,
  onSubmit,
  onSaved,
}: {
  kind: 'product' | 'category';
  initial: { name: string; slug: string; isActive: boolean } | null;
  onClose: () => void;
  onSubmit: (input: { name: string; slug: string; isActive: boolean }) => Promise<unknown>;
  onSaved: () => void;
}): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.metadata;
  const action = useAction();
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(initial));
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const effectiveSlug = slugTouched ? slug : toSlug(name);
  // The comma rule is the category's alone: its names are listed comma-separated
  // on an article and split again on the way to the reader, a product's never
  // are. Said here, before the request, in the words the schema would answer with.
  const nameError =
    name.trim().length === 0
      ? s.nameRequired
      : kind === 'category' && !isValidCategoryName(name)
        ? t.admin.rules.categoryNameRequirement
        : undefined;
  const slugError = !isValidSlug(effectiveSlug)
    ? t.admin.rules.slugRequirement
    : undefined;

  const submit = async () => {
    if (nameError || slugError) return;
    const result = await action.run(() =>
      onSubmit({ name: name.trim(), slug: effectiveSlug, isActive }),
    );
    if (result !== undefined) onSaved();
  };

  return (
    <Modal
      open
      title={initial ? s[kind].editTag(initial.name) : s[kind].newTag}
      onClose={onClose}
      busy={action.running}
      footer={
        <>
          <button type="button" className="button" onClick={onClose} disabled={action.running}>
            {s.cancel}
          </button>
          <button
            type="button"
            className="button button-primary"
            onClick={() => void submit()}
            disabled={action.running || Boolean(nameError || slugError)}
          >
            {action.running ? s.saving : s.save}
          </button>
        </>
      }
    >
      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}

      <Field
        label={s.name}
        htmlFor="tag-name"
        required
        error={nameError}
        hint={kind === 'category' ? s.categoryNameHint : undefined}
      >
        <input
          id="tag-name"
          type="text"
          value={name}
          maxLength={200}
          /* data-autofocus, not autoFocus - see the comment in Modal. */
          data-autofocus
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      {/*
        A product slug is no longer only a query parameter: it is the first
        segment of every article address under it, /kb/<product>/<article>, so
        editing one moves every article the product carries. That is a different
        order of consequence from a filter link going stale, and the two kinds of
        tag have to say so differently.
      */}
      <Field
        label={s.slug}
        htmlFor="tag-slug"
        required
        error={slugError}
        hint={s[kind].slugHint}
      >
        <input
          id="tag-slug"
          type="text"
          value={effectiveSlug}
          maxLength={200}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          onBlur={(event) => setSlug(toSlug(event.target.value))}
        />
      </Field>

      <div className="field">
        <label htmlFor="tag-active">{s.state}</label>
        <label className="inline-checkbox">
          <input
            id="tag-active"
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          <span>{s.active}</span>
        </label>
        <p className="field-hint">{s[kind].retireHint}</p>
      </div>
    </Modal>
  );
}
